import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { useMutation } from 'react-query';
import { assessmentService } from '../../services/assessmentService';
import MessageBubble from './MessageBubble';
import TypingIndicator from './TypingIndicator';
import ProgressBar from './ProgressBar';
import QuickActionButtons from './QuickActionButtons';

interface Message {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  quickActions?: string[];
}

const ChatInterface: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [assessmentId, setAssessmentId] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [currentQuickActions, setCurrentQuickActions] = useState<string[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Initialize chat with welcome message
    const welcomeMessage: Message = {
      id: Date.now().toString(),
      type: 'assistant',
      content: 'Ciao! Sono l\'AI Implementation Advisor. Ti aiuterò a valutare la preparazione della tua azienda per implementare AI Agents.\n\nIniziamo: qual è il nome della tua azienda?',
      timestamp: new Date()
    };
    setMessages([welcomeMessage]);
  }, []);

  const startAssessmentMutation = useMutation(
    (data: { companyName: string; sector: string; userRole: string }) =>
      assessmentService.startAssessment(data),
    {
      onSuccess: (data) => {
        setAssessmentId(data.assessmentId);
        const response: Message = {
          id: Date.now().toString(),
          type: 'assistant',
          content: data.question,
          timestamp: new Date(),
          quickActions: data.suggestedAnswers || []
        };
        setMessages(prev => [...prev, response]);
        setCurrentQuickActions(data.suggestedAnswers || []);
      },
      onError: (error) => {
        console.error('Error starting assessment:', error);
        const errorMessage: Message = {
          id: Date.now().toString(),
          type: 'assistant',
          content: 'Mi dispiace, c\'è stato un errore. Riprova tra qualche minuto.',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, errorMessage]);
      }
    }
  );

  const submitAnswerMutation = useMutation(
    (data: { assessmentId: string; question: string; answer: string }) =>
      assessmentService.submitAnswer(data.assessmentId, data.question, data.answer),
    {
      onSuccess: (data) => {
        setProgress(data.progress);
        setIsComplete(data.isComplete);

        if (data.isComplete) {
          navigate(`/results/${assessmentId}`);
        } else if (data.nextQuestion) {
          const response: Message = {
            id: Date.now().toString(),
            type: 'assistant',
            content: data.nextQuestion,
            timestamp: new Date(),
            quickActions: data.suggestedAnswers || []
          };
          setMessages(prev => [...prev, response]);
          setCurrentQuickActions(data.suggestedAnswers || []);
        }
      },
      onError: (error) => {
        console.error('Error submitting answer:', error);
        const errorMessage: Message = {
          id: Date.now().toString(),
          type: 'assistant',
          content: 'Mi dispiace, c\'è stato un errore nel processare la tua risposta. Puoi riprovare?',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, errorMessage]);
      }
    }
  );

  const handleQuickAction = (option: string) => {
    handleSubmitAnswer(option);
  };

  const handleSubmitAnswer = (answer: string) => {
    if (!answer.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: answer,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setCurrentQuickActions([]); // Clear quick actions after selection
    setIsLoading(true);

    // Count how many user messages we have to determine flow
    const userMessageCount = messages.filter((m: Message) => m.type === 'user').length + 1;

    if (!assessmentId) {
      // Multi-step onboarding process
      if (userMessageCount === 1) {
        // First answer is company name
        setTimeout(() => {
          const sectorQuestion: Message = {
            id: Date.now().toString(),
            type: 'assistant',
            content: 'Perfetto! In che settore operate?',
            timestamp: new Date(),
            quickActions: ['Fashion', 'Electronics', 'Food & Beverage', 'Home & Garden', 'B2B Services', 'Altro']
          };
          setMessages(prev => [...prev, sectorQuestion]);
          setCurrentQuickActions(sectorQuestion.quickActions || []);
          setIsLoading(false);
        }, 800);
      } else if (userMessageCount === 2) {
        // Second answer is sector, ask for role
        setTimeout(() => {
          const roleQuestion: Message = {
            id: Date.now().toString(),
            type: 'assistant',
            content: 'Ottimo! Qual è il tuo ruolo in azienda?',
            timestamp: new Date(),
            quickActions: ['CEO/Founder', 'CTO', 'CMO/Marketing Manager', 'Operations Manager', 'Altro']
          };
          setMessages(prev => [...prev, roleQuestion]);
          setCurrentQuickActions(roleQuestion.quickActions || []);
          setIsLoading(false);
        }, 800);
      } else if (userMessageCount === 3) {
        // Third answer is role, start assessment
        const userRole = answer;
        const companyName = messages.find((m: Message) => m.type === 'user')?.content || '';
        const sector = messages.filter((m: Message) => m.type === 'user')[1]?.content || '';

        startAssessmentMutation.mutate({ companyName, sector, userRole });
        setIsLoading(false);
      }
    } else {
      // Get the last assistant message as the question
      const lastAssistantMessage = messages
        .slice()
        .reverse()
        .find(msg => msg.type === 'assistant');

      if (lastAssistantMessage) {
        submitAnswerMutation.mutate({
          assessmentId,
          question: lastAssistantMessage.content,
          answer: answer
        });
      }
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    handleSubmitAnswer(inputValue);
    setInputValue('');
  };

  return (
    <div className="bg-white rounded-lg shadow-lg flex flex-col h-[600px]">
      {/* Progress bar */}
      <div className="p-4 border-b">
        <ProgressBar progress={progress} />
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar">
        {messages.map((message) => (
          <MessageBubble key={message.id} message={message} />
        ))}
        {isLoading && <TypingIndicator />}
        {!isLoading && currentQuickActions.length > 0 && (
          <QuickActionButtons
            options={currentQuickActions}
            onSelect={handleQuickAction}
            disabled={isLoading || isComplete}
          />
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-4 border-t">
        <form onSubmit={handleSubmit} className="flex space-x-2">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Scrivi la tua risposta..."
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
            disabled={isLoading || isComplete}
          />
          <button
            type="submit"
            disabled={!inputValue.trim() || isLoading || isComplete}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <PaperAirplaneIcon className="h-5 w-5" />
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChatInterface;